import * as THREE from 'three';
import { bakeColored, createInstancedMesh, mergeBufferGeometries, type InstanceTransform } from './batch';
import type { GlowSpot } from './glow';
import { makeSignTexture, mesh, roundedBox } from './helpers';
import type { ChannelMaterials, ParkMaterials } from './materials';
import {
  bakedDecor,
  bulbTransforms,
  buntingSwag,
  createTwinkle,
  haloSpots,
  nightDressing,
  pennantGeometry,
  ringPositions,
  signMedallion,
  starTopper,
  visualTier,
  type VisualTier,
  type XYZ,
} from './tiers';

const SAMPLES = 176;
const RAIL_OFFSET = 0.15;
const CAR_COUNT = 4;
const CAR_LENGTH = 0.64;

type Frames = {
  points: THREE.Vector3[];
  tangents: THREE.Vector3[];
  ups: THREE.Vector3[];
  sides: THREE.Vector3[];
};

/**
 * Compact oval: station straight in front (heading -x), low west turn, lift hill climbing along the
 * back straight (reads as a slope from the fixed camera), drop into the loop at the back-right, return turn.
 */
function createTrackCurve(): THREE.CatmullRomCurve3 {
  const points: THREE.Vector3[] = [
    new THREE.Vector3(2.6, 0.95, 0),
    new THREE.Vector3(1.2, 0.95, 0),
    new THREE.Vector3(-0.3, 0.95, 0),
    new THREE.Vector3(-1.9, 1.0, -0.25),
    new THREE.Vector3(-3.2, 1.15, -1.2),
    new THREE.Vector3(-3.7, 1.35, -2.5),
    new THREE.Vector3(-3.2, 1.7, -3.7),
    new THREE.Vector3(-2.0, 2.4, -4.3),
    new THREE.Vector3(-0.7, 3.05, -4.45),
    new THREE.Vector3(0.3, 2.4, -4.45),
    new THREE.Vector3(1.0, 1.4, -4.4),
  ];
  const loop = new THREE.Vector3(2.2, 0.95, -4.4);
  const radius = 1.15;
  for (let i = 0; i <= 14; i += 1) {
    const t = (i / 14) * Math.PI * 2;
    points.push(new THREE.Vector3(loop.x + radius * Math.sin(t), loop.y + radius * (1 - Math.cos(t)), loop.z + t * 0.02));
  }
  points.push(
    new THREE.Vector3(3.6, 1.1, -4.0),
    new THREE.Vector3(4.4, 1.4, -2.8),
    new THREE.Vector3(4.3, 1.3, -1.4),
    new THREE.Vector3(3.6, 1.05, -0.4),
  );
  return new THREE.CatmullRomCurve3(points, true, 'catmullrom', 0.3);
}

/** Parallel-transport frames seeded from world up, with the closing twist spread along the loop. */
function computeFrames(curve: THREE.CatmullRomCurve3, samples: number): Frames {
  const points: THREE.Vector3[] = [];
  const tangents: THREE.Vector3[] = [];
  const ups: THREE.Vector3[] = [];
  const sides: THREE.Vector3[] = [];
  const worldUp = new THREE.Vector3(0, 1, 0);
  for (let i = 0; i <= samples; i += 1) {
    const u = i / samples;
    points.push(curve.getPointAt(u));
    tangents.push(curve.getTangentAt(u).normalize());
  }
  const first = worldUp.clone().addScaledVector(tangents[0]!, -worldUp.dot(tangents[0]!)).normalize();
  ups.push(first);
  const axis = new THREE.Vector3();
  for (let i = 1; i <= samples; i += 1) {
    const previous = ups[i - 1]!.clone();
    axis.crossVectors(tangents[i - 1]!, tangents[i]!);
    if (axis.length() > 1e-6) {
      axis.normalize();
      const angle = Math.acos(THREE.MathUtils.clamp(tangents[i - 1]!.dot(tangents[i]!), -1, 1));
      previous.applyAxisAngle(axis, angle);
    }
    ups.push(previous.normalize());
  }
  const closing = ups[samples]!;
  let twist = Math.acos(THREE.MathUtils.clamp(closing.dot(ups[0]!), -1, 1));
  if (tangents[0]!.dot(new THREE.Vector3().crossVectors(closing, ups[0]!)) < 0) twist = -twist;
  for (let i = 0; i <= samples; i += 1) {
    ups[i]!.applyAxisAngle(tangents[i]!, (twist * i) / samples).normalize();
    // right-handed (side, up, tangent) so makeBasis() yields a proper rotation
    sides.push(new THREE.Vector3().crossVectors(ups[i]!, tangents[i]!).normalize());
  }
  return { points, tangents, ups, sides };
}

function tubeAlongFrames(frames: Frames, radius: number, sideOffset: number, upOffset: number, radial = 6): THREE.BufferGeometry {
  const count = frames.points.length;
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const center = new THREE.Vector3();
  const normal = new THREE.Vector3();
  for (let i = 0; i < count; i += 1) {
    center.copy(frames.points[i]!).addScaledVector(frames.sides[i]!, sideOffset).addScaledVector(frames.ups[i]!, upOffset);
    for (let j = 0; j <= radial; j += 1) {
      const theta = (j / radial) * Math.PI * 2;
      normal.copy(frames.sides[i]!).multiplyScalar(Math.cos(theta)).addScaledVector(frames.ups[i]!, Math.sin(theta));
      positions.push(center.x + normal.x * radius, center.y + normal.y * radius, center.z + normal.z * radius);
      normals.push(normal.x, normal.y, normal.z);
      uvs.push(i / (count - 1), j / radial);
    }
  }
  for (let i = 0; i < count - 1; i += 1) {
    for (let j = 0; j < radial; j += 1) {
      const a = i * (radial + 1) + j;
      const b = a + radial + 1;
      indices.push(a, a + 1, b, a + 1, b + 1, b);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

function createCarGeometries(mats: ParkMaterials): { body: THREE.BufferGeometry; trim: THREE.BufferGeometry; cabin: THREE.BufferGeometry } {
  const body = new THREE.Group();
  body.add(mesh(roundedBox(0.36, 0.19, CAR_LENGTH - 0.06, 0.05), mats.horseCoat, { position: [0, 0.13, 0] }));
  body.add(mesh(new THREE.SphereGeometry(0.15, 9, 7), mats.horseCoat, { position: [0, 0.11, 0.28], scale: [1.1, 0.75, 1] }));

  const trim = new THREE.Group();
  for (const x of [-0.185, 0.185]) {
    trim.add(mesh(new THREE.BoxGeometry(0.02, 0.055, 0.46), mats.horseCoat, { position: [x, 0.16, -0.02] }));
  }
  trim.add(mesh(new THREE.BoxGeometry(0.22, 0.035, 0.04), mats.horseCoat, { position: [0, 0.08, 0.38] }));

  const cabin = new THREE.Group();
  cabin.add(mesh(new THREE.BoxGeometry(0.34, 0.05, 0.44), mats.iron, { position: [0, 0.02, 0] }));
  for (const z of [-0.18, 0.16]) {
    for (const x of [-0.15, 0.15]) {
      cabin.add(mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.03, 6), mats.charcoal, { position: [x, 0.035, z], rotation: [0, 0, Math.PI / 2] }));
    }
  }
  cabin.add(mesh(new THREE.BoxGeometry(0.3, 0.06, 0.4), mats.charcoal, { position: [0, 0.22, -0.04] }));
  cabin.add(mesh(new THREE.BoxGeometry(0.3, 0.16, 0.05), mats.charcoal, { position: [0, 0.3, -0.24] }));
  const shirts = [mats.blue, mats.yellow];
  [-0.085, 0.085].forEach((x, index) => {
    cabin.add(mesh(new THREE.CapsuleGeometry(0.05, 0.07, 2, 5), shirts[index]!, { position: [x, 0.31, -0.08] }));
    cabin.add(mesh(new THREE.SphereGeometry(0.052, 7, 5), mats.skin, { position: [x, 0.43, -0.08] }));
    cabin.add(mesh(new THREE.SphereGeometry(0.055, 7, 5), index === 0 ? mats.woodDark : mats.orange, { position: [x, 0.45, -0.085], scale: [1, 0.6, 1] }));
  });
  return { body: bakeColored(body), trim: bakeColored(trim), cabin: bakeColored(cabin) };
}

function createSupports(frames: Frames, channels: ChannelMaterials): THREE.Group {
  const group = new THREE.Group();
  const posts: THREE.Matrix4[] = [];
  const beams: InstanceTransform[] = [];
  const ties: THREE.Matrix4[] = [];
  const feet: InstanceTransform[] = [];
  const dummy = new THREE.Object3D();
  const yAxis = new THREE.Vector3(0, 1, 0);
  const horizontal = new THREE.Vector3();
  const side = new THREE.Vector3();
  const base = new THREE.Vector3();
  const top = new THREE.Vector3();
  const direction = new THREE.Vector3();

  const addPost = (from: THREE.Vector3, to: THREE.Vector3, list: THREE.Matrix4[], thickness: number) => {
    direction.subVectors(to, from);
    const length = direction.length();
    dummy.position.copy(from).add(to).multiplyScalar(0.5);
    dummy.quaternion.setFromUnitVectors(yAxis, direction.normalize());
    dummy.scale.set(thickness, length, thickness);
    dummy.updateMatrix();
    list.push(dummy.matrix.clone());
  };

  for (let i = 0; i < frames.points.length - 1; i += 6) {
    const point = frames.points[i]!;
    const tangent = frames.tangents[i]!;
    const up = frames.ups[i]!;
    if (point.y < 0.45 || Math.abs(tangent.y) > 0.74 || up.y < 0.35) continue;
    if (point.z > -0.6 && point.x > -0.6 && point.x < 3.0) continue; // station has its own posts
    horizontal.set(tangent.x, 0, tangent.z).normalize();
    side.crossVectors(yAxis, horizontal).normalize();
    const clearance = point.y - 0.12;
    for (const sign of [-1, 1]) {
      base.copy(point).setY(0).addScaledVector(side, sign * (0.3 + clearance * 0.08));
      top.copy(point).setY(clearance).addScaledVector(side, sign * 0.24);
      addPost(base, top, posts, 1);
      feet.push({ position: [base.x, 0.05, base.z], rotation: [0, Math.atan2(horizontal.x, horizontal.z), 0] });
    }
    beams.push({
      position: [point.x, clearance - 0.02, point.z],
      rotation: [0, Math.atan2(side.x, side.z), 0],
      scale: [1, 1, 0.62 + clearance * 0.05],
    });
    if (clearance > 1.6) {
      const midY = clearance * 0.5;
      const left = base.copy(point).setY(midY).addScaledVector(side, -(0.3 + clearance * 0.08) * 0.5 - 0.12);
      const right = top.copy(point).setY(midY).addScaledVector(side, (0.3 + clearance * 0.08) * 0.5 + 0.12);
      addPost(left.clone(), right.clone(), ties, 0.7);
    }
  }

  const postMesh = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.085, 0.1, 1, 7), channels.secondary, posts.length);
  posts.forEach((matrix, index) => postMesh.setMatrixAt(index, matrix));
  postMesh.instanceMatrix.needsUpdate = true;
  postMesh.castShadow = true;
  postMesh.receiveShadow = true;
  group.add(postMesh);

  if (ties.length) {
    const tieMesh = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.055, 0.055, 1, 6), channels.secondary, ties.length);
    ties.forEach((matrix, index) => tieMesh.setMatrixAt(index, matrix));
    tieMesh.instanceMatrix.needsUpdate = true;
    tieMesh.castShadow = false;
    group.add(tieMesh);
  }
  group.add(createInstancedMesh(new THREE.BoxGeometry(0.11, 0.11, 1), channels.secondary, beams, { cast: false }));
  group.add(createInstancedMesh(new THREE.BoxGeometry(0.38, 0.1, 0.38), channels.structure, feet, { cast: false }));
  return group;
}

function createStation(mats: ParkMaterials, channels: ChannelMaterials, tier: VisualTier): THREE.Group {
  const station = new THREE.Group();
  const structure: THREE.BufferGeometry[] = [
    new THREE.BoxGeometry(3.1, 0.12, 0.42).translate(1.2, 0.74, 0),
    new THREE.BoxGeometry(3.1, 0.16, 0.86).translate(1.2, 0.8, 0.6),
    new THREE.BoxGeometry(1.0, 0.16, 0.3).translate(2.75, 0.62, 1.1),
    new THREE.BoxGeometry(1.0, 0.16, 0.3).translate(2.75, 0.42, 1.36),
    new THREE.BoxGeometry(1.0, 0.16, 0.3).translate(2.75, 0.22, 1.62),
  ];
  station.add(mesh(mergeBufferGeometries(structure), channels.structure));

  const secondary: THREE.BufferGeometry[] = [new THREE.BoxGeometry(3.3, 0.5, 1.15).translate(1.2, 0.25, 0.55)];
  for (const [x, z] of [
    [-0.15, -0.35],
    [2.55, -0.35],
    [-0.15, 1.0],
    [2.55, 1.0],
  ] as Array<[number, number]>) {
    secondary.push(new THREE.CylinderGeometry(0.06, 0.07, 1.4, 8).translate(x, 1.58, z));
  }
  secondary.push(new THREE.BoxGeometry(3.4, 0.1, 1.85).translate(1.2, 2.3, 0.32));
  secondary.push(new THREE.BoxGeometry(2.3, 0.04, 0.04).translate(0.85, 1.32, 1.02));
  for (let i = 0; i < 6; i += 1) {
    secondary.push(new THREE.CylinderGeometry(0.02, 0.02, 0.44, 5).translate(-0.25 + i * 0.44, 1.1, 1.02));
  }
  station.add(mesh(mergeBufferGeometries(secondary), channels.secondary));

  const roof = new THREE.ConeGeometry(1.0, 0.6, 4);
  roof.rotateY(Math.PI / 4);
  roof.scale(2.5, 1, 1.4);
  roof.translate(1.2, 2.65, 0.32);
  station.add(mesh(roof, channels.primary));
  if (tier >= 3) {
    station.add(starTopper(mats, 0.34, 3.08).translateX(1.2).translateZ(0.32));
  } else {
    station.add(mesh(new THREE.BoxGeometry(0.16, 0.16, 0.16), channels.accent, { position: [1.2, 3.0, 0.32] }));
  }

  const sign = mesh(
    new THREE.PlaneGeometry(1.5, 0.42),
    new THREE.MeshStandardMaterial({ map: makeSignTexture('LOOP', '#fff6e6', '#5a3a1c', '#f2c14e'), roughness: 0.6 }),
    { cast: false, receive: false },
  );
  sign.position.set(1.2, 2.06, 1.34);
  station.add(sign);

  const stationBulbs: XYZ[] =
    tier >= 2
      ? [
          ...[-0.1, 0.2, 0.5, 0.8, 1.1, 1.4, 1.7, 2.0, 2.3, 2.6].map((x) => [x, 2.2, 1.28] as XYZ),
          ...[-0.35, 2.85].flatMap((x) => [-0.5, 0.3, 1.1].map((z) => [x, 2.36, z] as XYZ)),
        ]
      : [0.2, 1.2, 2.2].map((x) => [x, 2.2, 1.28] as XYZ);
  station.add(createInstancedMesh(new THREE.SphereGeometry(0.07, 8, 6), channels.lights, bulbTransforms(stationBulbs), { cast: false, receive: false }));
  station.userData.bulbs = stationBulbs;
  station.add(mesh(new THREE.BoxGeometry(0.5, 0.42, 0.4), mats.butter, { position: [-0.05, 1.08, 0.6] }));
  station.add(mesh(new THREE.BoxGeometry(0.56, 0.06, 0.46), mats.red, { position: [-0.05, 1.32, 0.6] }));

  if (tier >= 2) {
    // Level 2: roof-corner pennants, gold ridge trim and a proper entrance arch with a medallion sign.
    const decor = new THREE.Group();
    for (const [x, z] of [
      [-0.35, -0.5],
      [2.75, -0.5],
      [-0.35, 1.15],
      [2.75, 1.15],
    ] as Array<[number, number]>) {
      decor.add(mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.4, 4), mats.iron, { position: [x, 2.5, z] }));
      decor.add(mesh(pennantGeometry(0.26, 0.15), x < 1 ? mats.red : mats.yellow, { position: [x, 2.7, z], cast: false }));
    }
    decor.add(mesh(new THREE.BoxGeometry(2.6, 0.06, 0.06), mats.gold, { position: [1.2, 3.0, 0.32], cast: false }));
    for (const x of [2.3, 3.2]) {
      decor.add(mesh(new THREE.CylinderGeometry(0.06, 0.07, 1.6, 8), mats.cream, { position: [x, 0.8, 1.95] }));
      decor.add(mesh(new THREE.SphereGeometry(0.09, 8, 6), mats.gold, { position: [x, 1.66, 1.95] }));
    }
    const arch = new THREE.TorusGeometry(0.45, 0.05, 6, 16, Math.PI);
    arch.translate(2.75, 1.62, 1.95);
    decor.add(mesh(arch, mats.red));
    const medallion = signMedallion(mats, 0.26, channels.primary);
    medallion.position.set(2.75, 2.3, 1.95);
    decor.add(medallion);
    if (tier >= 3) {
      decor.add(buntingSwag(mats, new THREE.Vector3(2.75, 2.5, 1.15), new THREE.Vector3(2.75, 2.5, 1.95), 4, 0.1));
      decor.add(buntingSwag(mats, new THREE.Vector3(-0.35, 2.5, 1.15), new THREE.Vector3(-0.35, 2.5, -0.5), 5, 0.12));
      decor.add(buntingSwag(mats, new THREE.Vector3(2.75, 2.5, -0.5), new THREE.Vector3(2.75, 2.5, 1.15), 5, 0.12));
    }
    station.add(bakedDecor(decor, mats));
  }
  return station;
}

export interface CoasterOptions {
  /** Upgrade level (1–3). Drives the COL-011B visual tier; footprint is unchanged. */
  level?: number;
}

export function createCoaster(mats: ParkMaterials, channels: ChannelMaterials, options: CoasterOptions = {}): THREE.Group {
  const tier = visualTier(options.level);
  const root = new THREE.Group();
  const curve = createTrackCurve();
  const frames = computeFrames(curve, SAMPLES);
  const curveLength = curve.getLength();

  const spine = new THREE.Mesh(tubeAlongFrames(frames, 0.07, 0, -0.1, 5), channels.structure);
  spine.castShadow = true;
  spine.receiveShadow = true;
  root.add(spine);
  const rails = new THREE.Mesh(
    mergeBufferGeometries([tubeAlongFrames(frames, 0.04, RAIL_OFFSET, 0, 5), tubeAlongFrames(frames, 0.04, -RAIL_OFFSET, 0, 5)]),
    channels.primary,
  );
  rails.castShadow = true;
  root.add(rails);

  const dummy = new THREE.Object3D();
  const tieStep = 3;
  const tieCount = Math.floor(frames.points.length / tieStep);
  const ties = new THREE.InstancedMesh(new THREE.BoxGeometry(0.4, 0.04, 0.1), channels.structure, tieCount);
  for (let i = 0; i < tieCount; i += 1) {
    const index = i * tieStep;
    dummy.position.copy(frames.points[index]!).addScaledVector(frames.ups[index]!, -0.03);
    dummy.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(frames.sides[index]!, frames.ups[index]!, frames.tangents[index]!));
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    ties.setMatrixAt(i, dummy.matrix);
  }
  ties.instanceMatrix.needsUpdate = true;
  ties.castShadow = true;
  root.add(ties);

  root.add(createSupports(frames, channels));
  const station = createStation(mats, channels, tier);
  root.add(station);

  let twinkle: ((elapsed: number) => void) | null = null;
  if (tier >= 2) {
    // Level 2 lights the lift hill and loop crest; level 3 strings bulbs along the whole track,
    // twinkles them and rings the loop with halos. Bulbs ride 0.2 above the rails on the frame up.
    const trackBulbs: XYZ[] = [];
    const step = tier >= 3 ? 5 : 6;
    for (let i = 0; i < frames.points.length - 1; i += step) {
      const point = frames.points[i]!;
      if (tier < 3 && point.y < 1.5) continue;
      if (point.z > -0.6 && point.x > -0.6 && point.x < 3.0) continue; // station has its own bulbs
      const up = frames.ups[i]!;
      trackBulbs.push([point.x + up.x * 0.2, point.y + up.y * 0.2, point.z + up.z * 0.2]);
    }
    const trackBulbMesh = createInstancedMesh(new THREE.SphereGeometry(0.06, 7, 5), channels.lights, bulbTransforms(trackBulbs), {
      cast: false,
      receive: false,
    });
    root.add(trackBulbMesh);
    if (tier >= 3) {
      twinkle = createTwinkle(trackBulbMesh, trackBulbs);
    }

    const stationBulbs = station.userData.bulbs as XYZ[];
    const halos: GlowSpot[] = [
      ...haloSpots(stationBulbs, 0.55),
      ...haloSpots(trackBulbs.filter((_, i) => i % 2 === 0), 0.5),
      { position: [2.75, 2.3, 1.95], size: 1.0 },
    ];
    if (tier >= 3) {
      halos.push(...haloSpots(ringPositions(12, 1.35, 0).map(([x, , z]) => [2.2 + x, 0.95 + 1.15 + z, -4.4] as XYZ), 0.7));
      halos.push({ position: [1.2, 3.2, 0.32], size: 1.3 });
    }
    const pools = nightDressing(mats, halos, { radius: tier >= 3 ? 3.0 : 2.4, y: 0.24 });
    pools.forEach((item) => {
      if (item.userData.lightPool) {
        item.position.x = 1.4;
        item.position.z = 0.8;
      }
    });
    root.add(...pools);
  }

  const carGeometry = createCarGeometries(mats);
  const bodies = new THREE.InstancedMesh(carGeometry.body, channels.primary, CAR_COUNT);
  const trims = new THREE.InstancedMesh(carGeometry.trim, channels.accent, CAR_COUNT);
  const cabins = new THREE.InstancedMesh(carGeometry.cabin, mats.baked, CAR_COUNT);
  bodies.castShadow = true;
  trims.castShadow = false;
  cabins.castShadow = true;
  root.add(bodies, trims, cabins);

  const carSpacing = CAR_LENGTH / curveLength;
  const point = new THREE.Vector3();
  const tangent = new THREE.Vector3();
  const up = new THREE.Vector3();
  const side = new THREE.Vector3();
  const basis = new THREE.Matrix4();
  let progress = 0.02;

  const poseCar = (index: number, u: number) => {
    const wrapped = ((u % 1) + 1) % 1;
    const sample = wrapped * SAMPLES;
    const i0 = Math.floor(sample);
    const i1 = Math.min(i0 + 1, SAMPLES);
    const t = sample - i0;
    point.lerpVectors(frames.points[i0]!, frames.points[i1]!, t);
    tangent.lerpVectors(frames.tangents[i0]!, frames.tangents[i1]!, t).normalize();
    up.lerpVectors(frames.ups[i0]!, frames.ups[i1]!, t).normalize();
    side.crossVectors(up, tangent).normalize();
    up.crossVectors(tangent, side).normalize();
    basis.makeBasis(side, up, tangent);
    dummy.position.copy(point).addScaledVector(up, 0.03);
    dummy.quaternion.setFromRotationMatrix(basis);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    bodies.setMatrixAt(index, dummy.matrix);
    trims.setMatrixAt(index, dummy.matrix);
    cabins.setMatrixAt(index, dummy.matrix);
  };

  let elapsed = 0;
  root.userData.update = (delta: number) => {
    const height = curve.getPointAt(progress).y;
    const speed = 0.028 + 0.05 * THREE.MathUtils.clamp(1 - (height - 0.9) / 3.2, 0, 1);
    progress = (progress + delta * speed) % 1;
    elapsed += delta;
    twinkle?.(elapsed);
    for (let i = 0; i < CAR_COUNT; i += 1) {
      poseCar(i, progress - i * carSpacing);
    }
    bodies.instanceMatrix.needsUpdate = true;
    trims.instanceMatrix.needsUpdate = true;
    cabins.instanceMatrix.needsUpdate = true;
  };
  root.userData.update(0);
  return root;
}
